'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ProgressBar from '@/components/tools/ProgressBar';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface OfferInput {
  companyName: string;
  jobTitle: string;
  baseSalary: number;
  signingBonus: number;
  annualBonusPct: number;
  equityAnnual: number;
  healthInsurance: number; // 1-5
  match401k: number; // percent
  ptoDays: number;
  remoteDays: number; // per week
  commuteMinutes: number;
  workLifeBalance: number; // 1-5
  growthOpportunity: number; // 1-5
}

interface OfferScore {
  totalComp: number;
  compScore: number; // 0-100
  benefitsScore: number; // 0-100
  workLifeScore: number; // 0-100
  growthScore: number; // 0-100
  overallScore: number; // 0-100
  compBreakdown: {
    base: number;
    bonus: number;
    equity: number;
    signing: number;
  };
}

interface ComparisonResults {
  scores: OfferScore[];
  winnerIndex: number;
  categoryWinners: {
    compensation: number;
    benefits: number;
    workLife: number;
    growth: number;
  };
  tips: string[];
  reasoning: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function emptyOffer(): OfferInput {
  return {
    companyName: '',
    jobTitle: '',
    baseSalary: 0,
    signingBonus: 0,
    annualBonusPct: 0,
    equityAnnual: 0,
    healthInsurance: 3,
    match401k: 0,
    ptoDays: 15,
    remoteDays: 0,
    commuteMinutes: 30,
    workLifeBalance: 3,
    growthOpportunity: 3,
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

/* ------------------------------------------------------------------ */
/*  Scoring algorithm                                                  */
/* ------------------------------------------------------------------ */

function scoreOffers(offers: OfferInput[]): ComparisonResults {
  /* --- Calculate raw numbers per offer --- */
  const rawScores: OfferScore[] = offers.map((o) => {
    const bonus = o.baseSalary * (o.annualBonusPct / 100);
    const signing = o.signingBonus / 4; // amortized over 4 years
    const totalComp = o.baseSalary + bonus + o.equityAnnual + signing;

    return {
      totalComp,
      compScore: 0,
      benefitsScore: 0,
      workLifeScore: 0,
      growthScore: 0,
      overallScore: 0,
      compBreakdown: {
        base: o.baseSalary,
        bonus,
        equity: o.equityAnnual,
        signing,
      },
    };
  });

  /* --- Normalize compensation 0-100 relative to best --- */
  const maxComp = Math.max(...rawScores.map((s) => s.totalComp), 1);
  rawScores.forEach((s) => {
    s.compScore = Math.round((s.totalComp / maxComp) * 100);
  });

  /* --- Benefits score: health (40%), 401k (30%), PTO (30%) --- */
  const maxPTO = Math.max(...offers.map((o) => o.ptoDays), 1);
  const max401k = Math.max(...offers.map((o) => o.match401k), 1);
  offers.forEach((o, i) => {
    const healthNorm = (o.healthInsurance / 5) * 100;
    const matchNorm = (o.match401k / Math.max(max401k, 6)) * 100; // cap at 6% as benchmark
    const ptoNorm = (o.ptoDays / Math.max(maxPTO, 25)) * 100; // cap at 25 days
    rawScores[i].benefitsScore = Math.round(
      healthNorm * 0.4 + matchNorm * 0.3 + ptoNorm * 0.3
    );
  });

  /* --- Work-life score: balance (40%), remote (30%), commute (30%) --- */
  const maxCommute = Math.max(...offers.map((o) => o.commuteMinutes), 1);
  offers.forEach((o, i) => {
    const balanceNorm = (o.workLifeBalance / 5) * 100;
    const remoteNorm = (o.remoteDays / 5) * 100;
    const commuteNorm = maxCommute > 0 ? ((maxCommute - o.commuteMinutes) / maxCommute) * 100 : 100;
    rawScores[i].workLifeScore = Math.round(
      balanceNorm * 0.4 + remoteNorm * 0.3 + clamp(commuteNorm, 0, 100) * 0.3
    );
  });

  /* --- Growth score --- */
  offers.forEach((o, i) => {
    rawScores[i].growthScore = Math.round((o.growthOpportunity / 5) * 100);
  });

  /* --- Overall weighted: comp 40%, benefits 20%, work-life 20%, growth 20% --- */
  rawScores.forEach((s) => {
    s.overallScore = Math.round(
      s.compScore * 0.4 +
        s.benefitsScore * 0.2 +
        s.workLifeScore * 0.2 +
        s.growthScore * 0.2
    );
  });

  /* --- Category winners --- */
  const best = (key: keyof OfferScore) => {
    let idx = 0;
    let max = -Infinity;
    rawScores.forEach((s, i) => {
      const v = s[key] as number;
      if (v > max) {
        max = v;
        idx = i;
      }
    });
    return idx;
  };

  const categoryWinners = {
    compensation: best('compScore'),
    benefits: best('benefitsScore'),
    workLife: best('workLifeScore'),
    growth: best('growthScore'),
  };

  const winnerIndex = best('overallScore');
  const winner = offers[winnerIndex];
  const winnerScore = rawScores[winnerIndex];

  /* --- Build reasoning --- */
  const parts: string[] = [];
  parts.push(
    `${winner.companyName || `Offer ${winnerIndex + 1}`} comes out ahead with an overall score of ${winnerScore.overallScore}/100.`
  );
  if (categoryWinners.compensation === winnerIndex) {
    parts.push(
      `It offers the highest total compensation at ${formatCurrency(winnerScore.totalComp)}/year.`
    );
  }
  if (categoryWinners.benefits === winnerIndex) {
    parts.push('It also leads in the benefits category.');
  }
  if (categoryWinners.workLife === winnerIndex) {
    parts.push('Work-life balance is strongest with this offer.');
  }
  if (categoryWinners.growth === winnerIndex) {
    parts.push('Growth opportunity is rated highest here.');
  }
  const reasoning = parts.join(' ');

  /* --- Personalized tips --- */
  const tips: string[] = [];

  // Comp variance
  const comps = rawScores.map((s) => s.totalComp);
  const compDelta = Math.max(...comps) - Math.min(...comps);
  if (compDelta > 20000) {
    tips.push(
      `There is a ${formatCurrency(compDelta)} gap in total compensation between your offers. Consider negotiating the lower offer up before accepting.`
    );
  }

  // Remote vs commute trade-off
  const remotes = offers.map((o) => o.remoteDays);
  if (Math.max(...remotes) - Math.min(...remotes) >= 3) {
    tips.push(
      'Remote flexibility varies significantly between offers. Factor in commute costs and time saved when working from home.'
    );
  }

  // Equity heavy
  offers.forEach((o, i) => {
    if (o.equityAnnual > o.baseSalary * 0.3) {
      tips.push(
        `${o.companyName || `Offer ${i + 1}`} has significant equity compensation. Evaluate the company's growth trajectory and vesting schedule carefully.`
      );
    }
  });

  // PTO difference
  const ptos = offers.map((o) => o.ptoDays);
  if (Math.max(...ptos) - Math.min(...ptos) >= 5) {
    tips.push(
      'PTO days differ notably. Remember that extra PTO has real monetary value (each day is roughly 0.4% of your annual salary).'
    );
  }

  // Growth vs comp trade-off
  if (categoryWinners.growth !== categoryWinners.compensation) {
    const growthWinner = offers[categoryWinners.growth];
    tips.push(
      `${growthWinner.companyName || `Offer ${categoryWinners.growth + 1}`} scores highest for growth but not compensation. Early-career professionals may benefit more from growth opportunities over a higher starting salary.`
    );
  }

  // 401k match
  const matches = offers.map((o) => o.match401k);
  if (Math.max(...matches) - Math.min(...matches) >= 3) {
    tips.push(
      '401(k) match differences can add up to thousands per year in free money. Don\'t overlook this when comparing offers.'
    );
  }

  if (tips.length === 0) {
    tips.push(
      'Your offers are fairly comparable. Focus on which company culture and role aligns best with your long-term career goals.'
    );
  }

  return { scores: rawScores, winnerIndex, categoryWinners, tips, reasoning };
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function InputField({
  label,
  id,
  value,
  onChange,
  type = 'number',
  prefix,
  suffix,
  hint,
  min,
  max,
  step,
}: {
  label: string;
  id: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: string;
  prefix?: string;
  suffix?: string;
  hint?: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">
            {prefix}
          </span>
        )}
        <input
          id={id}
          type={type}
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full border border-gray-300 rounded-xl py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-sm ${
            prefix ? 'pl-7 pr-3' : suffix ? 'pl-3 pr-8' : 'px-3'
          }`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">
            {suffix}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function RatingField({
  label,
  id,
  value,
  onChange,
  max = 5,
}: {
  label: string;
  id: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1">
        {label}: <span className="text-blue-600 font-bold">{value}/{max}</span>
      </label>
      <input
        id={id}
        type="range"
        min={1}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-blue-600 h-2 rounded-lg cursor-pointer"
      />
      <div className="flex justify-between text-xs text-gray-400 mt-0.5">
        <span>Poor</span>
        <span>Excellent</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stacked bar (pure CSS)                                             */
/* ------------------------------------------------------------------ */

const COMP_COLORS = {
  base: 'bg-blue-500',
  bonus: 'bg-indigo-400',
  equity: 'bg-purple-500',
  signing: 'bg-pink-400',
};

function CompensationChart({
  offers,
  scores,
  winnerIdx,
}: {
  offers: OfferInput[];
  scores: OfferScore[];
  winnerIdx: number;
}) {
  const maxComp = Math.max(...scores.map((s) => s.totalComp), 1);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
      <h3 className="text-xl font-bold text-gray-900 mb-6">
        Total Annual Compensation
      </h3>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-6 text-sm">
        {([
          ['Base Salary', COMP_COLORS.base],
          ['Annual Bonus', COMP_COLORS.bonus],
          ['Equity/RSU', COMP_COLORS.equity],
          ['Signing (annualized)', COMP_COLORS.signing],
        ] as const).map(([label, color]) => (
          <div key={label} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm ${color}`} />
            <span className="text-gray-600">{label}</span>
          </div>
        ))}
      </div>

      <div className="space-y-5">
        {scores.map((s, i) => {
          const isWinner = i === winnerIdx;
          const pctBase = (s.compBreakdown.base / maxComp) * 100;
          const pctBonus = (s.compBreakdown.bonus / maxComp) * 100;
          const pctEquity = (s.compBreakdown.equity / maxComp) * 100;
          const pctSigning = (s.compBreakdown.signing / maxComp) * 100;

          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-semibold ${isWinner ? 'text-green-700' : 'text-gray-700'}`}>
                  {offers[i].companyName || `Offer ${i + 1}`}
                  {isWinner && (
                    <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                      Highest
                    </span>
                  )}
                </span>
                <span className={`text-sm font-bold ${isWinner ? 'text-green-700' : 'text-gray-900'}`}>
                  {formatCurrency(s.totalComp)}
                </span>
              </div>
              <div className="w-full h-7 bg-gray-100 rounded-full overflow-hidden flex">
                <div
                  className={`${COMP_COLORS.base} h-full transition-all duration-700`}
                  style={{ width: `${pctBase}%` }}
                  title={`Base: ${formatCurrency(s.compBreakdown.base)}`}
                />
                <div
                  className={`${COMP_COLORS.bonus} h-full transition-all duration-700`}
                  style={{ width: `${pctBonus}%` }}
                  title={`Bonus: ${formatCurrency(s.compBreakdown.bonus)}`}
                />
                <div
                  className={`${COMP_COLORS.equity} h-full transition-all duration-700`}
                  style={{ width: `${pctEquity}%` }}
                  title={`Equity: ${formatCurrency(s.compBreakdown.equity)}`}
                />
                <div
                  className={`${COMP_COLORS.signing} h-full transition-all duration-700`}
                  style={{ width: `${pctSigning}%` }}
                  title={`Signing: ${formatCurrency(s.compBreakdown.signing)}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function OfferComparisonContent() {
  const [offers, setOffers] = useState<OfferInput[]>([emptyOffer(), emptyOffer()]);
  const [showThird, setShowThird] = useState(false);
  const [results, setResults] = useState<ComparisonResults | null>(null);

  const activeOffers = showThird ? offers : offers.slice(0, 2);

  function updateOffer(index: number, key: keyof OfferInput, raw: string | number) {
    setOffers((prev) => {
      const updated = [...prev];
      const offer = { ...updated[index] };
      if (typeof offer[key] === 'number') {
        (offer as Record<string, unknown>)[key] = Number(raw) || 0;
      } else {
        (offer as Record<string, unknown>)[key] = raw;
      }
      updated[index] = offer;
      return updated;
    });
  }

  function toggleThirdOffer() {
    if (!showThird && offers.length < 3) {
      setOffers((prev) => [...prev, emptyOffer()]);
    }
    setShowThird((prev) => !prev);
    setResults(null);
  }

  function handleCompare() {
    setResults(scoreOffers(activeOffers));
  }

  /* ---------------------------------------------------------------- */
  /*  Comparison table helpers                                         */
  /* ---------------------------------------------------------------- */

  type RowDef = {
    label: string;
    values: string[];
    winnerIdx: number;
    isHeader?: boolean;
  };

  function buildComparisonRows(): RowDef[] {
    if (!results) return [];
    const rows: RowDef[] = [];
    const s = results.scores;
    const o = activeOffers;

    const bestIdx = (vals: number[]) => {
      let idx = 0;
      vals.forEach((v, i) => {
        if (v > vals[idx]) idx = i;
      });
      return idx;
    };

    // Compensation
    rows.push({
      label: 'Total Compensation',
      values: s.map((sc) => formatCurrency(sc.totalComp)),
      winnerIdx: bestIdx(s.map((sc) => sc.totalComp)),
      isHeader: true,
    });
    rows.push({
      label: 'Base Salary',
      values: o.map((x) => formatCurrency(x.baseSalary)),
      winnerIdx: bestIdx(o.map((x) => x.baseSalary)),
    });
    rows.push({
      label: 'Annual Bonus',
      values: s.map((sc) => formatCurrency(sc.compBreakdown.bonus)),
      winnerIdx: bestIdx(s.map((sc) => sc.compBreakdown.bonus)),
    });
    rows.push({
      label: 'Equity/RSU (annual)',
      values: o.map((x) => formatCurrency(x.equityAnnual)),
      winnerIdx: bestIdx(o.map((x) => x.equityAnnual)),
    });
    rows.push({
      label: 'Signing Bonus',
      values: o.map((x) => formatCurrency(x.signingBonus)),
      winnerIdx: bestIdx(o.map((x) => x.signingBonus)),
    });

    // Benefits
    rows.push({
      label: 'Benefits Score',
      values: s.map((sc) => `${sc.benefitsScore}/100`),
      winnerIdx: results.categoryWinners.benefits,
      isHeader: true,
    });
    rows.push({
      label: 'Health Insurance',
      values: o.map((x) => `${x.healthInsurance}/5`),
      winnerIdx: bestIdx(o.map((x) => x.healthInsurance)),
    });
    rows.push({
      label: '401(k) Match',
      values: o.map((x) => `${x.match401k}%`),
      winnerIdx: bestIdx(o.map((x) => x.match401k)),
    });
    rows.push({
      label: 'PTO Days',
      values: o.map((x) => `${x.ptoDays} days`),
      winnerIdx: bestIdx(o.map((x) => x.ptoDays)),
    });

    // Work-life
    rows.push({
      label: 'Work-Life Score',
      values: s.map((sc) => `${sc.workLifeScore}/100`),
      winnerIdx: results.categoryWinners.workLife,
      isHeader: true,
    });
    rows.push({
      label: 'Remote Days/Week',
      values: o.map((x) => `${x.remoteDays} days`),
      winnerIdx: bestIdx(o.map((x) => x.remoteDays)),
    });
    rows.push({
      label: 'Commute Time',
      values: o.map((x) => `${x.commuteMinutes} min`),
      winnerIdx: bestIdx(o.map((x) => -x.commuteMinutes)), // lower is better
    });
    rows.push({
      label: 'Work-Life Balance',
      values: o.map((x) => `${x.workLifeBalance}/5`),
      winnerIdx: bestIdx(o.map((x) => x.workLifeBalance)),
    });

    // Growth
    rows.push({
      label: 'Growth Score',
      values: s.map((sc) => `${sc.growthScore}/100`),
      winnerIdx: results.categoryWinners.growth,
      isHeader: true,
    });
    rows.push({
      label: 'Growth Opportunity',
      values: o.map((x) => `${x.growthOpportunity}/5`),
      winnerIdx: bestIdx(o.map((x) => x.growthOpportunity)),
    });

    return rows;
  }

  /* ---------------------------------------------------------------- */
  /*  Offer form card                                                  */
  /* ---------------------------------------------------------------- */

  function OfferCard({ index }: { index: number }) {
    const o = offers[index];
    const prefix = `offer${index}`;

    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-sm font-bold">
            {index + 1}
          </span>
          Offer {index + 1}
        </h3>

        <div className="space-y-4">
          {/* Company & Title */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="Company Name"
              id={`${prefix}-company`}
              type="text"
              value={o.companyName}
              onChange={(v) => updateOffer(index, 'companyName', v)}
            />
            <InputField
              label="Job Title"
              id={`${prefix}-title`}
              type="text"
              value={o.jobTitle}
              onChange={(v) => updateOffer(index, 'jobTitle', v)}
            />
          </div>

          {/* Compensation */}
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-2">
            Compensation
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="Base Salary"
              id={`${prefix}-base`}
              value={o.baseSalary || ''}
              onChange={(v) => updateOffer(index, 'baseSalary', v)}
              prefix="$"
              min={0}
            />
            <InputField
              label="Signing Bonus"
              id={`${prefix}-signing`}
              value={o.signingBonus || ''}
              onChange={(v) => updateOffer(index, 'signingBonus', v)}
              prefix="$"
              min={0}
            />
            <InputField
              label="Annual Bonus %"
              id={`${prefix}-bonus`}
              value={o.annualBonusPct || ''}
              onChange={(v) => updateOffer(index, 'annualBonusPct', v)}
              suffix="%"
              min={0}
              max={100}
              hint="Percentage of base salary"
            />
            <InputField
              label="Equity/RSU Value (annual)"
              id={`${prefix}-equity`}
              value={o.equityAnnual || ''}
              onChange={(v) => updateOffer(index, 'equityAnnual', v)}
              prefix="$"
              min={0}
              hint="Annual vesting value"
            />
          </div>

          {/* Benefits */}
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-2">
            Benefits
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <RatingField
              label="Health Insurance Quality"
              id={`${prefix}-health`}
              value={o.healthInsurance}
              onChange={(v) => updateOffer(index, 'healthInsurance', v)}
            />
            <InputField
              label="401(k) Match"
              id={`${prefix}-401k`}
              value={o.match401k || ''}
              onChange={(v) => updateOffer(index, 'match401k', v)}
              suffix="%"
              min={0}
              max={100}
            />
            <InputField
              label="PTO Days Per Year"
              id={`${prefix}-pto`}
              value={o.ptoDays || ''}
              onChange={(v) => updateOffer(index, 'ptoDays', v)}
              min={0}
              max={365}
            />
            <InputField
              label="Remote Days Per Week"
              id={`${prefix}-remote`}
              value={o.remoteDays || ''}
              onChange={(v) => updateOffer(index, 'remoteDays', v)}
              min={0}
              max={5}
            />
          </div>

          {/* Work-life & Growth */}
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-2">
            Work-Life &amp; Growth
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="Commute Time (one way)"
              id={`${prefix}-commute`}
              value={o.commuteMinutes || ''}
              onChange={(v) => updateOffer(index, 'commuteMinutes', v)}
              suffix="min"
              min={0}
            />
            <div /> {/* spacer */}
            <RatingField
              label="Work-Life Balance"
              id={`${prefix}-wlb`}
              value={o.workLifeBalance}
              onChange={(v) => updateOffer(index, 'workLifeBalance', v)}
            />
            <RatingField
              label="Growth Opportunity"
              id={`${prefix}-growth`}
              value={o.growthOpportunity}
              onChange={(v) => updateOffer(index, 'growthOpportunity', v)}
            />
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  const comparisonRows = results ? buildComparisonRows() : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Job Offer Comparison Tool"
        description="Compare 2-3 job offers side by side. Calculate total compensation including salary, bonus, equity, benefits, and work-life factors to find the best fit."
        category="Career Tools"
      />

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Toggle third offer */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Enter Your Offers</h2>
          <button
            type="button"
            onClick={toggleThirdOffer}
            className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition flex items-center gap-1"
          >
            {showThird ? (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                </svg>
                Remove 3rd Offer
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add 3rd Offer
              </>
            )}
          </button>
        </div>

        {/* Offer cards */}
        <div className={`grid gap-6 ${showThird ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-2'}`}>
          <OfferCard index={0} />
          <OfferCard index={1} />
          {showThird && <OfferCard index={2} />}
        </div>

        {/* Compare button */}
        <div className="mt-8">
          <button
            onClick={handleCompare}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg cursor-pointer"
          >
            Compare Offers
          </button>
        </div>

        {/* ================================================================ */}
        {/*  Results                                                         */}
        {/* ================================================================ */}
        {results && (
          <div className="mt-12 space-y-10">
            {/* Overall recommendation */}
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl shadow-lg p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-200 text-green-800">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <div>
                  <h2 className="text-2xl font-extrabold text-green-900 mb-1">
                    Recommended: {activeOffers[results.winnerIndex].companyName || `Offer ${results.winnerIndex + 1}`}
                    {activeOffers[results.winnerIndex].jobTitle && (
                      <span className="text-lg font-semibold text-green-700 ml-2">
                        ({activeOffers[results.winnerIndex].jobTitle})
                      </span>
                    )}
                  </h2>
                  <p className="text-green-800 leading-relaxed">{results.reasoning}</p>
                </div>
              </div>
            </div>

            {/* Category scores with ProgressBars */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Category Scores
              </h3>

              <div className="space-y-8">
                {(
                  [
                    { key: 'compScore', label: 'Compensation', winnerKey: 'compensation' },
                    { key: 'benefitsScore', label: 'Benefits', winnerKey: 'benefits' },
                    { key: 'workLifeScore', label: 'Work-Life Balance', winnerKey: 'workLife' },
                    { key: 'growthScore', label: 'Growth Opportunity', winnerKey: 'growth' },
                    { key: 'overallScore', label: 'Overall', winnerKey: null },
                  ] as const
                ).map(({ key, label, winnerKey }) => (
                  <div key={key}>
                    <p className="text-sm font-bold text-gray-800 mb-3">{label}</p>
                    <div className="space-y-2">
                      {results.scores.map((s, i) => {
                        if (i >= activeOffers.length) return null;
                        const isWinner =
                          winnerKey !== null
                            ? results.categoryWinners[winnerKey] === i
                            : results.winnerIndex === i;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span
                              className={`text-xs font-semibold w-28 truncate ${
                                isWinner ? 'text-green-700' : 'text-gray-600'
                              }`}
                            >
                              {activeOffers[i].companyName || `Offer ${i + 1}`}
                              {isWinner && ' *'}
                            </span>
                            <div className="flex-1">
                              <ProgressBar
                                value={s[key] as number}
                                label=""
                                showValue={true}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Compensation chart */}
            <CompensationChart
              offers={activeOffers}
              scores={results.scores}
              winnerIdx={results.categoryWinners.compensation}
            />

            {/* Side-by-side comparison table */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 overflow-x-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Detailed Comparison
              </h3>

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 pr-4 font-semibold text-gray-600 w-40">
                      Category
                    </th>
                    {activeOffers.map((o, i) => (
                      <th key={i} className="text-center py-3 px-2 font-bold text-gray-900">
                        {o.companyName || `Offer ${i + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, ri) => (
                    <tr
                      key={ri}
                      className={`border-b border-gray-100 ${
                        row.isHeader ? 'bg-gray-50' : ''
                      }`}
                    >
                      <td
                        className={`py-2.5 pr-4 ${
                          row.isHeader
                            ? 'font-bold text-gray-900'
                            : 'text-gray-600 pl-4'
                        }`}
                      >
                        {row.label}
                      </td>
                      {row.values.map((val, ci) => {
                        const isWinner = ci === row.winnerIdx;
                        return (
                          <td
                            key={ci}
                            className={`py-2.5 px-2 text-center font-medium ${
                              isWinner
                                ? 'text-green-700 bg-green-50 font-bold'
                                : 'text-gray-800'
                            } ${row.isHeader ? 'font-bold' : ''}`}
                          >
                            {val}
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Overall row */}
                  <tr className="border-t-2 border-gray-300 bg-blue-50">
                    <td className="py-3 pr-4 font-extrabold text-gray-900">
                      Overall Score
                    </td>
                    {results.scores.slice(0, activeOffers.length).map((s, i) => {
                      const isWinner = i === results.winnerIndex;
                      return (
                        <td
                          key={i}
                          className={`py-3 px-2 text-center font-extrabold text-lg ${
                            isWinner
                              ? 'text-green-700 bg-green-100'
                              : 'text-gray-800'
                          }`}
                        >
                          {s.overallScore}/100
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Things to consider */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 sm:p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Things to Consider
              </h3>
              <ul className="space-y-3">
                {results.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700 leading-relaxed">
                    <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-amber-500" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Ready to land your best offer?"
              description="A standout resume gets you more offers to choose from. Build a professional, ATS-friendly resume in minutes with Superpower Resume."
              buttonText="Build My Resume Free"
            />
          </div>
        )}

        {/* Educational Content */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">How to Compare Job Offers: Beyond Base Salary</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              When evaluating competing job offers, base salary is only one piece of the puzzle. <strong>Total compensation</strong> includes annual bonuses, stock options or RSUs, signing bonuses, 401(k) matching, health insurance value, and other monetary benefits. According to the{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics</a>,
              benefits can account for over 30% of total employee compensation in the United States, meaning two offers with identical salaries can differ by tens of thousands of dollars in actual value.
            </p>
            <p>
              <strong>Equity compensation</strong> requires special attention. Stock options and RSUs at a publicly traded company have a clear market value, while equity at a private startup carries significant uncertainty. Evaluate the vesting schedule (typically four years with a one-year cliff), the company&apos;s funding stage, and realistic exit scenarios. The{' '}
              <a href="https://www.careeronestop.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop</a>{' '}
              resource from the U.S. Department of Labor provides tools for researching salary benchmarks and benefits across industries and regions.
            </p>
            <p>
              Do not overlook <strong>non-monetary factors</strong> such as remote flexibility, commute time, PTO, work-life balance, and career growth potential. Research from{' '}
              <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review</a>{' '}
              shows that factors like learning opportunities and managerial quality have a stronger correlation with long-term career satisfaction than initial salary. A structured comparison that weighs all these dimensions — as this tool provides — helps you make a decision you will be confident in years from now.
            </p>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics Occupational Outlook Handbook</a>
              {' · '}
              <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: Career Planning</a>
              {' · '}
              <a href="https://www.careeronestop.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop — U.S. Department of Labor</a>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
